// Re-export shared types from workflowSharedTypes
export {
    EntityType,
    entityTypeValues,
    WorkflowDefinitionType,
    WorkflowStateType,
    ReminderType,
    reminderTypeValues,
    reminderAutomationValues,
    TimingOrientation,
    timingOrientationValues,
    WorkflowTransitionContent,
    AdditionalContent,
    LinkType,
} from './workflowSharedTypes';

import {
    LinkType,
    TimingOrientation,
    AdditionalContent,
    ReminderType,
    WorkflowStateType,
    WorkflowDefinitionType,
    EntityType,
    WorkflowTransitionContent,
} from './workflowSharedTypes';

// Local interfaces for workflowsSortAndFilters.ts
// These have different structure than workflow/types.ts interfaces
export interface WorkflowAction {
    Name: string;
    Icon?: [string, string];
    NewStateOption?: string;
}

export interface WorkflowDefinitionTemplate {
    Name?: string;
    EntityType?: EntityType | string;
    Type?: WorkflowDefinitionType;
    AutoClose?: boolean;
    AutoCloseTrigger?: string;
    States?: WorkflowStateTemplate[] | string[];
}

export interface WorkflowStateTemplate {
    Name?: string;
    Type?: string;
    Automation?: string;
    ReminderType?: string;
    NotDueGap?: number | undefined;
    IsEntryOrExit?: boolean | undefined;
    DeliveryMethod?: number | undefined;
    Transitions?: WorkflowTransitionTemplate[] | string[];
    IncludeCanceledInstallments?: boolean;
    _fixed?: boolean;
    _canMoveUp?: boolean;
    _canMoveDown?: boolean;
}

export interface WorkflowTransitionTemplate {
    Name?: string;
    Trigger?: string;
    NewState?: string;
    _linkType?: LinkType;
    Timing?: {
        Gap?: number;
        Orientation?: TimingOrientation;
    };
}

export interface WorkflowTemplateOption {
    Name: string;
    IsCustomerWorkflow?: boolean;
    IsPaymentPlanRelated?: boolean;
    WorkflowDefinition: WorkflowDefinitionTemplate;
    AvailableActions: string[];
    RequiredMinReminders?: number;
    InsertIdx: number;
}

export interface WorkflowStateTemplateOption {
    Name: string;
    IsCustomerWorkflow?: boolean;
    DisplayName?: string;
    Description?: string;
    ReadOnlyFields?: string[];
    FormFields?: string[];
    HasContent?: boolean;
    State: WorkflowStateTemplate;
    MaxInstanceAllowed?: number;
}

export interface WorkflowTransitionTemplateOption {
    Name: string;
    Transition: WorkflowTransitionTemplate;
    Contents?: WorkflowTransitionContent[];
    AdditionalContents?: AdditionalContent[];
}

export const linkTypeValues = [LinkType.FirstNotDue, LinkType.FirstOverdue];

// [NOTE] can be customized by returning from back-end instead of hardcoding
export const workflowTransitionTemplateOptions: WorkflowTransitionTemplateOption[] = [
    {
        Name: 'New Not Due Invoices',
        Transition: {
            Timing: {
                Gap: -7,
                Orientation: TimingOrientation.DueDate
            },
            Trigger: 'New Not Due Invoices',
            _linkType: LinkType.FirstNotDue,
        },
        AdditionalContents: [AdditionalContent.LinkTypeSelection]
    },
    {
        Name: 'New Overdue Invoices',
        Transition: {
            Timing: {
                Gap: 1,
                Orientation: TimingOrientation.DueDate
            },
            Trigger: 'New Overdue Invoices',
            _linkType: LinkType.FirstOverdue,
        },
        AdditionalContents: [AdditionalContent.LinkTypeSelection]
    },
    {

        Name: 'New Customers',
        Transition: {
            NewState: 'Pending State Move',
            Timing: {
                Gap: 0,
                Orientation: TimingOrientation.LastAction
            },
            Trigger: 'New Customers'
        }
    },
    {

        Name: 'Move To Not Due',
        Transition: {
            Timing: {
                Gap: -7,
                Orientation: TimingOrientation.DueDate
            },
            Trigger: 'Move To Not Due',
            _linkType: LinkType.FirstNotDue,
        },
        AdditionalContents: [AdditionalContent.LinkTypeSelection]
    },
    {
        Name: 'Move To Overdue',
        Transition: {
            Timing: {
                Gap: 1,
                Orientation: TimingOrientation.DueDate
            },
            Trigger: 'Move To Overdue',
            _linkType: LinkType.FirstOverdue,
        },
        AdditionalContents: [AdditionalContent.LinkTypeSelection]
    },
    {
        Name: 'Escalate To Collections',
        Transition: {
            Trigger: 'Escalate To Collections',
            NewState: 'Pending Settlement'
        }
    },
    {
        Name: 'Send Automated Communication',
        Transition: {
            Trigger: 'Send Automated Communication',
            Timing: {
                Gap: 7,
                Orientation: TimingOrientation.LastAction
            },
            _linkType: LinkType.NextState
        }
    },
    {
        Name: 'Send Manual Communication',
        Transition: {
            Trigger: 'Send Manual Communication',
            Timing: {
                Gap: 7,
                Orientation: TimingOrientation.LastAction
            },
            _linkType: LinkType.NextState
        }
    },
    {
        Name: 'Action Phone Call',
        Transition: {
            Trigger: 'Action Phone Call',
            Timing: {
                Gap: 7,
                Orientation: TimingOrientation.LastAction
            },
            _linkType: LinkType.NextState
        }
    },
    {
        Name: 'New Payment Schedule',
        Transition: {
            Trigger: 'New Payment Schedule',
            Timing: {
                Gap: 0,
                Orientation: TimingOrientation.DueDate
            },
            _linkType: LinkType.NextState
        }
    },
    {
        Name: 'Cancel Payment Plan',
        Transition: {
            Trigger: 'Cancel Payment Plan',
            NewState: 'Closed'
        }
    },
    {
        Name: 'Leave It Open',
        Transition: {
            Trigger: 'Leave It Open',
            NewState: 'Closed'
        }
    }
];

// [NOTE] can be customized by returning from back-end instead of hardcoding
export const workflowStateTemplateOptions: WorkflowStateTemplateOption[] = [
    {
        Name: 'New Invoices',
        IsCustomerWorkflow: false,
        State: {
            Name: 'New',
            IsEntryOrExit: true,
            Transitions: [
                'New Not Due Invoices',
                'New Overdue Invoices'
            ],
            _fixed: true,
            _canMoveUp: false,
            _canMoveDown: false
        },
        DisplayName: 'New Invoices',
        Description: 'This is the starting point for all new invoices. From here, invoices are monitored until they either become overdue or remain not due. Configure timing to control when transitions to reminders begin.',
    },
    {
        Name: 'New Customers',
        IsCustomerWorkflow: true,
        State: {
            Name: 'New',
            IsEntryOrExit: true,
            Transitions: [
                'New Customers'
            ],
            _fixed: true,
            _canMoveUp: false,
            _canMoveDown: false
        },
        DisplayName: 'New Customers',
        Description: 'Used at the beginning of a customer-specific workflow. This state tracks newly added customers before any automated or manual follow-up begins.',
    },
    {
        Name: 'Pending State Move',
        State: {
            Name: 'Pending State Move',
            IsEntryOrExit: false,
            Transitions: [
                'Move To Not Due',
                'Move To Overdue'
            ],
            _fixed: true,
            _canMoveUp: false,
            _canMoveDown: false,
        },
        DisplayName: 'State transition',
        Description: 'This state is used to hold invoices temporarily before deciding whether they should transition to overdue or not-due reminders.',
    },
    {
        Name: 'Escalate To Collections',
        State: {
            Name: 'Escalate To Collections',
            IsEntryOrExit: false,
            ReminderType: ReminderType.Overdue,
            Type: WorkflowStateType.Collection,
            Automation: 'Automatic', // Added for matching with WorkflowDefinitionMapping
            Transitions: [
                'Escalate To Collections'
            ],
            _fixed: true,
            _canMoveUp: false,
            _canMoveDown: false
        },
        Description: 'This step is used when an account needs to be escalated to collections. It typically follows multiple reminders or missed payments, and transitions to a final state like Pending Settlement.',
        FormFields: ['EmailContact'],
    },
    {
        Name: 'Pending Settlement',
        State: {
            Name: 'Pending Settlement',
            IsEntryOrExit: true,
            _fixed: true,
            _canMoveUp: false,
            _canMoveDown: false
        },
        Description: 'This is an endpoint for invoices that have been escalated or resolved through collections. No further automated actions occur from here.',
    },
    {
        Name: 'Overdue Reminder',
        State: {
            Name: 'Overdue Reminder',
            ReminderType: ReminderType.Overdue,
            Type: WorkflowStateType.Communication,
            Automation: 'Automatic',
            IsEntryOrExit: false,
            Transitions: [
                'Send Automated Communication'
            ],
            _canMoveDown: true,
            _canMoveUp: true,
            _fixed: false
        },
        Description: 'A flexible step for sending automated reminders once an invoice is overdue. Timing is configurable. Add multiple instances to escalate progressively.',
        ReadOnlyFields: ['ReminderType', 'Automation'],
        FormFields: ['Name', 'ReminderType', 'Automation', 'EmailContact'],
        HasContent: true
    },
    {
        Name: 'Manual Reminder',
        State: {
            Name: 'Manual Reminder',
            Type: WorkflowStateType.Communication,
            Automation: 'Manual',
            IsEntryOrExit: false,
            Transitions: [
                'Send Manual Communication'
            ],
            _canMoveDown: true,
            _canMoveUp: true,
            _fixed: false
        },
        Description: 'A manual step for sending personalised reminders when automation isn’t suitable. Ideal for special or case-by-case follow-ups.',
        ReadOnlyFields: ['Automation'],
        FormFields: ['IsSecondaryState', 'Name', 'ReminderType', 'Automation', 'NotDueGap', 'EmailContact'],
        HasContent: true
    },
    {
        Name: 'Payment Reminder',
        State: {
            Name: 'Payment Reminder',
            ReminderType: ReminderType.NotDue,
            Type: WorkflowStateType.Communication,
            Automation: 'Automatic',
            IsEntryOrExit: false,
            Transitions: [
                'Send Automated Communication'
            ],
            _canMoveDown: true,
            _canMoveUp: true,
            _fixed: false
        },
        Description: 'Used to send proactive reminders before an invoice becomes overdue. Only one instance is allowed, and it helps reduce overdue rates by notifying early.',
        ReadOnlyFields: ['ReminderType'],
        FormFields: ['Name', 'ReminderType', 'NotDueGap', 'EmailContact'],
        HasContent: true,
        MaxInstanceAllowed: 1
    },
    {
        Name: 'Phone Call',
        State: {
            Name: 'Phone Call',
            Type: WorkflowStateType.PhoneCall,
            IsEntryOrExit: false,
            Automation: 'Manual',
            Transitions: [
                'Action Phone Call'
            ],
            _canMoveDown: true,
            _canMoveUp: true,
            _fixed: false,
        },
        Description: 'This step involves manual outreach (e.g., a phone call) as a follow-up after an automated reminder. After completion, it typically transitions to the next overdue step.',
        FormFields: ['IsSecondaryState', 'Name', 'ReminderType', 'NotDueGap']
    },
    {
        Name: 'New Payment Schedule',
        State: {
            Name: 'New',
            Type: WorkflowStateType.PaymentSchedule,
            IsEntryOrExit: true,
            Transitions: [
                'New Payment Schedule'
            ],
            _canMoveDown: false,
            _canMoveUp: false,
            _fixed: true,
        },
        DisplayName: 'New Payment Schedule',
        Description: 'Marks the beginning of a structured payment plan. This entry point is for setting up instalments or alternative payment arrangements for overdue accounts.'
    },
    {
        Name: 'Instalment',
        State: {
            Name: 'Instalment',
            Type: WorkflowStateType.PaymentSchedule,
            ReminderType: ReminderType.Overdue,
            IsEntryOrExit: false,
            Transitions: [
                'Send Automated Communication'
            ],
            _canMoveDown: true,
            _canMoveUp: true,
            _fixed: false,
        },
        Description: 'Represents an individual instalment in a payment schedule. Automatically triggered reminders can follow up on missed instalments.',
        ReadOnlyFields: ['ReminderType'],
        FormFields: ['Name', 'ReminderType', 'EmailContact'],
        HasContent: true
    },
    {
        Name: 'Cancel Payment Plan',
        State: {
            Name: 'Cancel Payment Plan',
            Type: WorkflowStateType.PaymentSchedule,
            ReminderType: ReminderType.Overdue,
            IsEntryOrExit: false,
            Transitions: [
                'Cancel Payment Plan',
                'Leave It Open'
            ],
            _canMoveDown: false,
            _canMoveUp: false,
            _fixed: true,
        },
        Description: 'Used to terminate a payment plan due to non-compliance or cancellation. You can configure this step to either leave the account open or escalate further.',
        FormFields: ['EmailContact'],
    }
];

// [NOTE] can be customized by returning from back-end instead of hardcoding
export const workflowActions: WorkflowAction[] = [
    { Name: 'Payment Reminder', NewStateOption: 'Payment Reminder' },
    { Name: 'Overdue Reminder', NewStateOption: 'Overdue Reminder' },
    { Name: 'Phone Call', NewStateOption: 'Phone Call' },
    { Name: 'Manual Reminder', NewStateOption: 'Manual Reminder' },
    { Name: 'Payment Instalment', NewStateOption: 'Instalment' }
];

// [NOTE] can be customized by returning from back-end instead of hardcoding
export const workflowTemplateOptions: WorkflowTemplateOption[] = [
    {
        Name: 'Default Invoice Reminder',
        IsCustomerWorkflow: false,
        RequiredMinReminders: 1,
        WorkflowDefinition: {
            EntityType: EntityType.Invoice,
            Type: WorkflowDefinitionType.Communication,
            States: [
                'New Invoices',
                'Pending State Move',
                'Escalate To Collections',
                'Pending Settlement'
            ],
        },
        AvailableActions: [
            'Payment Reminder',
            'Overdue Reminder',
            'Phone Call',
            'Manual Reminder'
        ],
        InsertIdx: -3
    },
    {
        Name: 'Default Customer Reminder',
        IsCustomerWorkflow: true,
        RequiredMinReminders: 1,
        WorkflowDefinition: {
            EntityType: EntityType.Customer,
            Type: WorkflowDefinitionType.Communication,
            States: [
                'New Customers',
                'Pending State Move',
                'Escalate To Collections',
                'Pending Settlement'
            ],
        },
        AvailableActions: [
            'Payment Reminder',
            'Overdue Reminder',
            'Phone Call',
            'Manual Reminder'
        ],
        InsertIdx: -3
    },
    //{
    //    Name: 'Default Payment Instalments',
    //    IsPaymentPlanRelated: true,
    //    RequiredMinReminders: 1,
    //    WorkflowDefinition: {
    //        EntityType: EntityType.PaymentPlanInstallment,
    //        Type: WorkflowDefinitionType.Communication,
    //        States: [
    //            'New Payment Schedule',
    //            'Instalment',
    //            'Cancel Payment Plan',
    //            'Pending Settlement'
    //        ],
    //    },
    //    AvailableActions: ['Payment Instalment'],
    //    InsertIdx: -3
    //}
];

export const PENDING_STATE_MOVE = 'Pending State Move';
export const PAYMENT_REMINDER = 'Payment Reminder';

export const TRANSITION_SYNCHRONIZATION_MAP: Record<string, string> = {
  'New Not Due Invoices': 'Move To Not Due',
  'New Overdue Invoices': 'Move To Overdue'
};

export const workflowChangesFilters = {
    ALL_CHANGES: 'All changes',
};

export const workflowChangeTypes = {
    NewResource: 'NewResource',
    UpdatedResource: 'UpdatedResource',
};

export const workflowChangesTableFilterOptions = [
    {
        label: 'All changes',
        value: workflowChangesFilters.ALL_CHANGES,
    },
];